import type { Ref } from "react";

import { CaretRight } from "@/components/icons/caret-right";
import { Close } from "@/components/icons/close";

type ContextMenuBaseProps = {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  /**
   * When set on the block settings overlay, attaches to the inner surface so nested
   * panels can portal here and cover the full panel (header + body).
   */
  layoutSurfaceRef?: Ref<HTMLDivElement>;
  /**
   * `sidePanel` — full-height right sheet (default).
   * `stack` — `absolute inset-0` sheet for use inside `layoutSurfaceRef` (e.g. portaled).
   */
  variant?: "sidePanel" | "stack";
};

type ContextMenuProps =
  | (ContextMenuBaseProps & {
      backLabel: string;
      onBack: () => void;
    })
  | (ContextMenuBaseProps & {
      backLabel?: never;
      onBack?: never;
    });

const overlayHeaderClassName =
  "flex h-12 shrink-0 items-center justify-between border-b border-solid border-gray-300 p-4 text-sm font-medium text-black";

export const ContextMenuOverlay = ({
  title,
  children,
  backLabel,
  onBack,
  onClose,
  layoutSurfaceRef,
  variant = "sidePanel",
}: ContextMenuProps) => {
  const header = (
    <div className={overlayHeaderClassName}>
      {onBack ? (
        <button
          type="button"
          aria-label={backLabel}
          className="flex min-w-0 items-center gap-x-2"
          onClick={onBack}
        >
          <CaretRight className="size-4 rotate-180 text-gray-800" />
          <span className="truncate">{title}</span>
        </button>
      ) : (
        <div className="flex min-w-0 items-center">
          <span className="truncate">{title}</span>
        </div>
      )}
      <button aria-label="Close" onClick={onClose}>
        <Close className="size-5 text-gray-700" />
      </button>
    </div>
  );

  const body = (
    <div
      data-content-hub-settings-scroll
      className="scrollbar-width-none relative min-h-0 flex-1 overflow-x-hidden overflow-y-scroll"
    >
      {children}
    </div>
  );

  if (variant === "stack") {
    return (
      <div className="absolute inset-0 z-[25] flex min-h-0 flex-col overflow-hidden bg-white">
        {header}
        {body}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-10 flex min-h-0 w-full min-w-0 flex-col overflow-hidden bg-white">
      <div
        ref={layoutSurfaceRef}
        className="scrollbar-width-none relative flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden"
      >
        {header}
        {body}
      </div>
    </div>
  );
};
